import type { IndicatorSource } from '../../lib/api';

/**
 * The §F5.6 footer's last clause: what the numbers above it actually are.
 *
 * It read `legend.footDemo` — "demo data (seed)" — unconditionally. That was true when Part
 * L was the only source of values, and became a **false statement** the day §M3 landed:
 * `b21` and `c21` carry real MELS reporting for 2025–26 at country and watershed level, and
 * the legend labelled it a simulation. The reverse mistake is just as bad, so an indicator
 * holding both gets neither label but a third one that says so.
 *
 * `derived` is M4's third real answer: `spi` carries SPI-3 computed here from the CHIRPS
 * archive (§E6), which is neither reporting nor simulation nor a hand upload, and calling it
 * "source not recorded" would understate a number whose lineage is the most complete of the
 * four.
 *
 * Driven by `/indicators.sources`, which is the provenance of what is *stored*, not the
 * catalogue's declaration of where the indicator is supposed to come from (`source`) — the
 * two disagree for exactly the indicators this is about. `undefined` is "the catalogue has
 * not landed yet", which reads as unknown rather than as an assertion either way.
 */
export function provenanceKey(sources: IndicatorSource[] | undefined): string {
  if (!sources || sources.length === 0) return 'legend.footUnknown';
  if (sources.length === 1) {
    if (sources[0] === 'demo') return 'legend.footDemo';
    if (sources[0] === 'mels') return 'legend.footMels';
    if (sources[0] === 'manual') return 'legend.footManual';
    if (sources[0] === 'derived') return 'legend.footDerived';
    return 'legend.footUnknown';
  }
  return sources.includes('demo') ? 'legend.footMixed' : 'legend.footMels';
}

/**
 * The §F5.2 picker's per-row provenance chip — the same question as `provenanceKey`, asked
 * where the reader is *choosing* rather than reading.
 *
 * A one-word answer, not the legend's sentence: the row already carries a name, a unit and
 * a domain, and a fourth clause would crowd all three out. So `MELS` means every stored
 * value came from the feed, `Demo` means none did, and a mixed series is labelled `MELS`
 * with the mixture named in the tooltip — because the honest short answer to "is this real"
 * for a series that is partly real is yes, and the legend below the map is where the
 * qualification belongs.
 *
 * `null` for an indicator with no values at all, and for one whose catalogue entry has not
 * landed: an absent chip is "nothing to say", which is exactly the case in both.
 */
export type ProvenanceChip = { kind: 'mels' | 'demo'; labelKey: string; titleKey: string };

export function provenanceChip(sources: IndicatorSource[] | undefined): ProvenanceChip | null {
  if (!sources || sources.length === 0) return null;
  if (sources.includes('mels')) {
    return {
      kind: 'mels',
      labelKey: 'indicators.chipMels',
      titleKey: sources.includes('demo')
        ? 'indicators.chipMixedTitle'
        : 'indicators.chipMelsTitle',
    };
  }
  if (sources.includes('demo')) {
    return { kind: 'demo', labelKey: 'indicators.chipDemo', titleKey: 'indicators.chipDemoTitle' };
  }
  return null;
}
